/**
 * Проверка «а поможет ли вообще перезагрузка».
 *
 * Автоперезагрузка после падения чанка исходит из того, что чанк пропал из-за выката: свежий HTML
 * назовёт живые хеши, и всё починится. Но ровно так же — ненайденным модулем — выглядит обрыв
 * связи: человек в метро тапает по ещё не загруженному роуту, `import()` не доезжает, и мы
 * перезагружаем ему страницу в туннеле, то есть меняем экран ошибки на браузерное «нет
 * соединения» и теряем приложение целиком.
 *
 * Отличить одно от другого можно: каждая отрендеренная страница несёт `<meta name="build">`
 * (см. `head()` в `routes/__root.tsx`). Спрашиваем сервер, какая сборка у него сейчас, и сверяем
 * со своей. Другая сборка — перезагрузка оправдана. Та же — не поможет, показываем честную ошибку.
 * Сервер не ответил — значит дело в сети, и перезагружаться тем более нельзя.
 */

export type ServedBuild = 'new-build' | 'same-build' | 'unreachable';

const PROBE_TIMEOUT_MS = 3_000;

/**
 * Сколько байт ответа читаем, прежде чем сдаться. Мета лежит в `<head>`, а целиком SSR-страница
 * весит под 160 КБ — качать их ради одной строки на мобильном канале незачем.
 */
const PROBE_BYTE_BUDGET = 64 * 1024;

// Порядок атрибутов в отрендеренном теге не гарантирован, поэтому два выражения, а не одно.
const BUILD_META_NAME_FIRST = /<meta[^>]+name=["']build["'][^>]+content=["']([^"']*)["']/i;
const BUILD_META_CONTENT_FIRST = /<meta[^>]+content=["']([^"']*)["'][^>]+name=["']build["']/i;

export const parseBuildFromHtml = (html: string): string | undefined =>
  BUILD_META_NAME_FIRST.exec(html)?.[1] || BUILD_META_CONTENT_FIRST.exec(html)?.[1] || undefined;

export const readCurrentBuild = (): string | undefined => {
  if (typeof document === 'undefined') {
    return undefined;
  }

  return document.querySelector<HTMLMetaElement>('meta[name="build"]')?.content || undefined;
};

/** Читает начало ответа и бросает остальное: нам нужен только `<head>`. */
const readHead = async (response: Response, budget: number): Promise<string> => {
  const reader = response.body?.getReader?.();

  if (!reader) {
    // Тела в виде потока нет (мок, старый движок) — читаем как есть.
    return response.text();
  }

  const decoder = new TextDecoder();
  let html = '';

  try {
    for (;;) {
      // Чтение потока последовательно по определению: следующий кусок нельзя запросить, не
      // дождавшись предыдущего, а весь смысл здесь — остановиться пораньше.
      // eslint-disable-next-line no-await-in-loop
      const { done, value } = await reader.read();

      if (done) {
        break;
      }

      html += decoder.decode(value, { stream: true });

      // Мета встретилась или бюджет исчерпан — дочитывать нечего.
      if (html.length >= budget || parseBuildFromHtml(html)) {
        break;
      }
    }
  } finally {
    // Обрываем загрузку остатка: иначе браузер честно докачает все 160 КБ.
    void reader.cancel().catch(() => {});
  }

  return html;
};

type ProbeInput = {
  url?: string;
  current?: string | undefined;
  timeoutMs?: number;
  fetchImpl?: typeof fetch;
};

export const probeServedBuild = async ({
  url = '/',
  current = readCurrentBuild(),
  timeoutMs = PROBE_TIMEOUT_MS,
  fetchImpl = typeof fetch === 'function' ? fetch : undefined,
}: ProbeInput = {}): Promise<ServedBuild> => {
  // Своей сборки не знаем (страница пришла не из нашего SSR) — сравнивать не с чем, и гадать
  // на пустом месте хуже, чем показать ошибку.
  if (!current || !fetchImpl) {
    return 'unreachable';
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetchImpl(url, {
      // Кеш браузера тут всё испортил бы: он с радостью отдаст ту же старую страницу.
      // Cloudflare страницу не кеширует (`cache-control: no-cache, no-store, must-revalidate`,
      // `cf-cache-status: BYPASS` — замерено на проде), так что ответ всегда живой.
      cache: 'no-store',
      // Именно `same-origin`, не `omit`: dev закрыт basic-auth, и без учётных данных ответ был
      // бы 401 без меты, то есть проверка на dev не работала бы вовсе. Побочного эффекта нет —
      // SSR ставит cookie локали только тогда, когда её не прислали, а у любой живой вкладки
      // она уже есть (её выставил тот же SSR при первой загрузке).
      credentials: 'same-origin',
      headers: { accept: 'text/html' },
      signal: controller.signal,
    });

    // Код ответа не проверяем: страница-404 тоже отрендерена нашим SSR и тоже несёт мету.
    const served = parseBuildFromHtml(await readHead(response, PROBE_BYTE_BUDGET));

    if (!served) {
      return 'unreachable';
    }

    return served === current ? 'same-build' : 'new-build';
  } catch {
    // Обрыв, таймаут, оффлайн — всё это «перезагружаться нельзя».
    return 'unreachable';
  } finally {
    clearTimeout(timer);
  }
};
